<template>
  <div class="carousel-wrapper">
    <!-- 단일 배너 실험이 활성화된 경우 -->
    <div
        v-if="experimentBanner"
        class="single-banner-container"
        @click="onBoxSelected(experimentBanner)"
    >
      <ShimmerImage
          :src="experimentBanner.imageUrl"
          :imageStyle="{ width: '100vw', maxWidth: '480px', maxHeight: '378.6px' }"
          :heightRatio="0.7889"
          :isCircle="false"
          :borderRadius="'0px'"
          :requestWidth="720"
      />
    </div>

    <!-- 실험이 없거나 기본 배너 표시 그룹인 경우 -->
    <template v-else>
      <SliderControls
          :currentPanelIndex="currentPanelIndex"
          :totalCarousel="filteredPromotionBanners.length"
      />

      <Swiper
          v-if="filteredPromotionBanners.length"
          ref="swiperRef"
          :modules="swiperModules"
          :loop="true"
          :slides-per-view="1"
          :speed="400"
          :threshold="10"
          :autoplay="{ delay: 3500, disableOnInteraction: false }"
          :touch-events-target="'container'"
          :touch-start-prevent-default="false"
          @slideChange="handleSlideChange"
          @touchStart="handleTouchStart"
      >
        <SwiperSlide
            v-for="(banner, index) in filteredPromotionBanners"
            :key="index"
        >
          <div class="carousel-item" @click="onBoxSelected(banner)">
            <ShimmerImage
                v-if="banner.imageUrl"
                :src="banner.imageUrl"
                :imageStyle="{ width: '100vw', maxWidth: '480px', maxHeight: '378.6px' }"
                :heightRatio="0.7889"
                :isCircle="false"
                :borderRadius="'0px'"
                :requestWidth="720"
            />
          </div>
        </SwiperSlide>
      </Swiper>

      <!-- 배너가 없는 경우 대체 UI -->
      <div v-else class="empty-banner">
        배너를 불러오는 중입니다...
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import ShimmerImage from '@/tarot_box/components/shimmer/ShimmerImage.vue';
import { logEvent } from '@/tarot_box/helper/native_api';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay } from 'swiper/modules';
import SliderControls from '@/tarot_box/pages/home/components/main_carousel/SliderControls.vue';
import { gbKey } from '@/utils/growthbook';

import 'swiper/css';

export default {
  name: 'MainCarousel',
  components: { SliderControls, ShimmerImage, Swiper, SwiperSlide },
  props: { totalCarousel: Number },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const eventBus = inject('eventBus');
    const growthbook = inject(gbKey);

    const swiperRef = ref(null);
    const currentPanelIndex = ref(1);
    const isTouching = ref(false);
    const experimentBanner = ref(null);
    const variationId = ref(null);
    const swiperModules = [Autoplay];

    // $isWeb 값 가져오기
    const isWeb = computed(() => {
      const instance = getCurrentInstance();
      return instance?.appContext.config.globalProperties.$isWeb() || false;
    });

    const promotionBanners = computed(() => {
      return (store.getters.promotionBanners || [])
          .map(b => (b._custom?.value || b))
          .filter(b => b && typeof b.imageUrl === 'string');
    });

    // 플랫폼에 따른 필터링된 배너
    const filteredPromotionBanners = computed(() => {
      return promotionBanners.value.filter(banner => {
        if (isWeb.value) {
          return banner.web === true;
        }
        return banner.app === true;
      });
    });

    const setupExperimentBanner = () => {
      if (!growthbook || !growthbook.ready) return;

      const result = growthbook.feature('main-carousel-banner');
      if (!result.on || !result.value) return;

      console.log('result', result);

      variationId.value = result.variation;
      experimentBanner.value = {
        id: parseInt(result.value.tarotBoxId, 10) || 0,
        title: result.value.title || '타로 리딩',
        imageUrl: result.value.imageUrl,
        linkType: result.value.linkType || 'tarot_box_detail',
        linkUrl: `/tarot_box/home?tarot_box_id=${result.value.tarotBoxId}`,
        tarotBoxId: result.value.tarotBoxId,
      };
    };

    const onBoxSelected = (banner) => {
      if (!banner || !growthbook || !growthbook.ready) return;

      const bannerData = banner;
      let tarotBoxId = bannerData.tarotBoxId;
      if (!tarotBoxId && bannerData.linkUrl) {
        tarotBoxId = new URL(bannerData.linkUrl, window.location.origin)
            .searchParams.get('tarot_box_id');
      }

      const result = growthbook.feature('main-carousel-banner');
      const experiment_id = result.experiment?.key;
      const variation_id = result.experimentResult?.variationId != null
          ? String(result.experimentResult.variationId)
          : null;

      const experimentProps = { experiment_id, variation_id };

      const baseEvent = {
        ...experimentProps,
        link_type: bannerData.linkType,
        banner_id: bannerData.id,
        tarot_box_id: tarotBoxId,
        tarot_box_name: bannerData.title
      };

      switch (bannerData.linkType) {
        case 'tarot_box_detail':
          logEvent('home_promo_banner_click', baseEvent);
          logEvent('home_contents_click', {
            ...baseEvent,
            click_origin_name: 'promotion_banner',
            click_origin: 'promotion_banner'
          });
          eventBus.emit('openTarotDetail', tarotBoxId);
          break;

        case 'inner_link': {
          const targetUrl = new URL(bannerData.linkUrl, window.location.origin);
          const currentParams = { ...route.query };

          targetUrl.searchParams.forEach((value, key) => {
            currentParams[key] = value;
          });

          const eventData = { ...experimentProps, link_type: bannerData.linkType };

          const category = targetUrl.searchParams.get('category');
          if (category) {
            const isNumeric = /^\d+$/.test(category);
            if (isNumeric) eventData.category_id = category;
            else eventData.category_name = category;
          }

          logEvent('home_promo_banner_click', eventData);

          router.push({
            path: targetUrl.pathname,
            query: currentParams
          });
          break;
        }

        case 'outer_link':
          window.open(bannerData.linkUrl);
          logEvent('home_promo_banner_click', {
            ...experimentProps,
            external_link: bannerData.linkUrl
          });
          break;

        default:
          logEvent('home_promo_banner_click', experimentProps);
      }
    };

    const handleTouchStart = () => {
      isTouching.value = true;
    };

    const handleSlideChange = (swiper) => {
      currentPanelIndex.value = (swiper.realIndex ?? 0) + 1;
      if (isTouching.value) {
        const result = growthbook.feature('main-carousel-banner');
        logEvent('home_promo_banner_swipe', {
          experiment_id: result.key,
          variation_id: result.variation != null ? String(result.variation) : null,
        });
        isTouching.value = false;
      }
    };

    onMounted(() => {
      setupExperimentBanner();
      watch(
          () => growthbook.ready,
          ready => ready && setupExperimentBanner()
      );
    });

    return {
      swiperRef,
      swiperModules,
      currentPanelIndex,
      filteredPromotionBanners,
      experimentBanner,
      onBoxSelected,
      handleTouchStart,
      handleSlideChange,
    };
  },
};
</script>

<style scoped>
.carousel-wrapper {
  position: relative;
  width: 100vw;
  max-width: 480px;
  height: calc(100vw * 0.7889);
  max-height: calc(480px * 0.7889);
  margin: 0;
  padding: 0;
  background-color: transparent;
  overflow: hidden;
}

.carousel-item {
  width: 100%;
  height: calc(100vw * 0.7889);
  max-height: calc(480px * 0.7889);
}

.single-banner-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-banner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>
